import { isBlockedForProspecting } from "./crm-v2-policy.mjs";
import {
  normalizeClient,
  matchBySiret,
  createSignal,
  reactivateFormerClient,
  scheduleWorkingDayTask,
  recalculateScores,
  logCommercialEvent,
  currentCommercial,
} from "./commercial.mjs";
import { todayParis, dateValue } from "../utils/calendar.mjs";
export const importColumns = {
  siret: "SIRET",
  name: "Société / établissement",
  city: "Ville",
  postalCode: "Code postal",
  sector: "Secteur",
  phone: "Téléphone",
  job: "Métier recherché",
  qty: "Nombre de postes",
  type: "Type de besoin",
  source: "Source",
  date: "Date du signal",
  contactName: "Nom du contact",
  contactRole: "Fonction",
  contactPhone: "Téléphone contact",
  contactEmail: "Email contact",
  notes: "Commentaire",
  closed: "Établissement fermé",
};
const aliases = {
  siret: ["siret"],
  name: [
    "societe",
    "etablissement",
    "societeetablissement",
    "raisonsociale",
    "nom",
  ],
  city: ["ville"],
  postalCode: ["codepostal", "cp"],
  sector: ["secteur"],
  phone: ["telephone", "tel"],
  job: ["metier", "metierrecherche"],
  qty: ["nombre", "nombredepostes", "postes"],
  type: ["typedebesoin", "type"],
  source: ["source"],
  date: ["datedusignal", "datesignal", "date"],
  contactName: ["nomducontact", "nomcontact", "contact"],
  contactRole: ["fonction"],
  contactPhone: ["telephonecontact", "telcontact"],
  contactEmail: ["emailcontact", "mailcontact"],
  notes: ["commentaire", "notes"],
  closed: ["etablissementferme", "ferme", "statutetablissement"],
};
const key = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
export function guessMapping(headers) {
  return Object.fromEntries(
    Object.keys(importColumns).map((field) => [
      field,
      headers.findIndex((h) => aliases[field]?.includes(key(h))) >= 0
        ? String(headers.findIndex((h) => aliases[field]?.includes(key(h))))
        : "",
    ]),
  );
}
export function parseCsv(text) {
  text = text.replace(/^\uFEFF/, "");
  const first = text.split(/\r?\n/)[0];
  const delimiter = [";", ",", "\t"].sort(
    (a, b) => first.split(b).length - first.split(a).length,
  )[0];
  const rows = [];
  let row = [],
    cell = "",
    quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (quoted && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else quoted = !quoted;
    } else if (ch === delimiter && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((ch === "\n" || ch === "\r") && !quoted) {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      if (row.some((x) => x.trim())) rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (quoted) throw new Error("CSV invalide : guillemets non fermés.");
  row.push(cell);
  if (row.some((x) => x.trim())) rows.push(row);
  return rows;
}
export function mapRows(rows, mapping) {
  return rows
    .slice(1)
    .filter((row) => row.some((x) => x !== null && String(x).trim()))
    .map((row, index) =>
      Object.fromEntries([
        ["_line", index + 2],
        ...Object.keys(importColumns).map((field) => [
          field,
          mapping[field] !== "" && mapping[field] !== undefined
            ? (row[Number(mapping[field])] ?? "")
            : "",
        ]),
      ]),
    );
}
export function validSiret(siret, strict = false) {
  if (!/^\d{14}$/.test(siret)) return false;
  if (!strict) return true;
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    let n = Number(siret[i]);
    if (i % 2 === 0) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return sum % 10 === 0;
}
export function analyzeImport(state, rows, options = {}) {
  const required = options.required || ["siret", "name"];
  const seen = new Set(state.clients.map((c) => String(c.siret)));
  return rows.map((row, index) => {
    const normalized = {
      ...row,
      siret: String(row.siret || "").replace(/\s/g, ""),
      name: String(row.name || "").trim(),
      date: dateValue(row.date),
      qty: row.qty === "" ? null : Number(row.qty),
    };
    let action = "nouveau",
      error = "";
    if (!normalized.siret) {
      action = "siret manquant";
      error = "SIRET manquant : ligne à corriger avant import.";
    } else if (!validSiret(normalized.siret, options.strict)) {
      action = "siret invalide";
      error = options.strict
        ? "SIRET invalide (format ou clé de contrôle)."
        : "Le SIRET doit comporter 14 chiffres.";
    } else if (
      required.some((field) =>
        field === "siret"
          ? !normalized.siret
          : !String(normalized[field] ?? "").trim(),
      )
    ) {
      action = "incomplet";
      error = "Minimum requis non satisfait.";
    } else if (row.date && !normalized.date) {
      action = "incomplet";
      error = "Date du signal invalide.";
    } else if (
      row.qty !== "" &&
      row.qty !== undefined &&
      row.qty !== null &&
      (!Number.isInteger(normalized.qty) || normalized.qty < 1)
    ) {
      action = "incomplet";
      error = "Le nombre de postes doit être un entier positif.";
    } else if (
      ["oui", "true", "1", "ferme", "fermé", "cessation"].includes(
        String(row.closed).toLowerCase(),
      ) ||
      matchBySiret(state.clients, normalized.siret)?.closed
    ) {
      action = "fermé";
      error = "Établissement déclaré fermé : non importé.";
    } else if (seen.has(normalized.siret)) action = "enrichi";
    seen.add(normalized.siret);
    return { row: normalized, line: row._line || index + 2, action, error };
  });
}
export function importProspects(state, rows, options = {}, now = new Date()) {
  const report = analyzeImport(state, rows, options);
  let next = state;
  const stats = {
    newProspects: 0,
    enriched: 0,
    reactivated: 0,
    reconquest: 0,
    missingSiret: 0,
    invalidSiret: 0,
    closed: 0,
    incomplete: 0,
  };
  for (const item of report) {
    if (item.error) {
      const field = {
        "siret manquant": "missingSiret",
        "siret invalide": "invalidSiret",
        fermé: "closed",
        incomplet: "incomplete",
      }[item.action];
      stats[field]++;
      continue;
    }
    const r = item.row;
    let c = matchBySiret(next.clients, r.siret);
    if(c && isBlockedForProspecting(c)){item.error="Établissement exclu de la prospection";item.action="exclu";stats.excluded=(stats.excluded||0)+1;continue;}
    if (!c) {
      c = normalizeClient({
        id: crypto.randomUUID(),
        siret: r.siret,
        name: r.name || `Établissement ${r.siret}`,
        city: r.city,
        postalCode: r.postalCode,
        sector: r.sector,
        phone: r.phone,
        status: "PROSPECT",
        kind: "prospect",
        contacts: [],
        history: [],
        agency: options.agency || currentCommercial.agency,
        owner: options.owner || currentCommercial.name,
        ownerId: options.ownerId || currentCommercial.id,
      });
      next = { ...next, clients: [...next.clients, c] };
      stats.newProspects++;
    } else {
      stats.enriched++;
      const enrich = {};
      for (const field of ["city", "postalCode", "sector", "phone"])
        if (!c[field] && r[field]) enrich[field] = r[field];
      next = {
        ...next,
        clients: next.clients.map((x) =>
          x.id === c.id ? { ...x, ...enrich } : x,
        ),
      };
    }
    if (r.contactName) {
      const existing = c.contacts.find(
        (x) => x.name.toLowerCase() === String(r.contactName).toLowerCase(),
      );
      const contact = existing
        ? {
            ...existing,
            role: existing.role || r.contactRole,
            phone: existing.phone || r.contactPhone,
            email: existing.email || r.contactEmail,
          }
        : {
            id: crypto.randomUUID(),
            name: String(r.contactName),
            lastName: String(r.contactName),
            firstName: "",
            role: r.contactRole,
            phone: r.contactPhone,
            email: r.contactEmail,
            active: true,
            primary: !c.contacts.some((x) => x.active && x.primary),
            inactiveReason: "",
            unavailableDays: [],
          };
      next = {
        ...next,
        clients: next.clients.map((x) =>
          x.id === c.id
            ? {
                ...x,
                contacts: existing
                  ? x.contacts.map((y) => (y.id === existing.id ? contact : y))
                  : [...x.contacts, contact],
              }
            : x,
        ),
      };
    }
    next = createSignal(
      next,
      c.id,
      {
        source: String(r.source || options.source || "Fichier de prospection"),
        date: r.date || todayParis(now),
        job: String(r.job || ""),
        qty: r.qty,
        contractType: r.type,
        content:
          r.notes ||
          `${r.qty || ""} ${r.job || "Signal de prospection"}`.trim(),
        type: "recrutement",
      },
      now,
    );
    if (["CLIENT DORMANT", "À RÉACTIVER"].includes(c.status)) {
      next = reactivateFormerClient(next, c.id, now);
      stats.reactivated++;
    } else if (["CLIENT PERDU", "À RECONQUÉRIR"].includes(c.status)) {
      next = reactivateFormerClient(next, c.id, now);
      stats.reconquest++;
    }
    const title =
      c.status === "CLIENT PERDU"
        ? "Reconquête : nouveau signal"
        : c.kind !== "prospect"
          ? "Nouveau signal — client connu"
          : "Qualifier le nouveau signal";
    if (
      !next.tasks.some((t) => !t.done && t.clientId === c.id && t.signalImport)
    )
      next = scheduleWorkingDayTask(
        next,
        c.id,
        title,
        todayParis(now),
        "call",
        { signalImport: true },
        now,
      );
    next = logCommercialEvent(
      next,
      c.id,
      "import",
      "Établissement enrichi sans doublon à l’import",
      { siret: r.siret },
      now,
    );
  }
  next = {
    ...next,
    imports: [
      ...next.imports,
      {
        id: crypto.randomUUID(),
        createdAt: new Date(now).toISOString(),
        stats,
        lines: report.length,
      },
    ],
  };
  return { state: recalculateScores(next, now), stats, report };
}
